/**
 * What the two public policy pages say.
 *
 * Separated from `Legal.tsx` the way the guide separates `content.tsx` from
 * its page, and for a stronger reason here: this is the one text on the site
 * where a sentence that sounds right and is not true is a problem rather than
 * a weak paragraph. Keeping it in one flat structure means it can be read end
 * to end and checked against the code without a component in the way.
 *
 * **Every factual claim below is one this repository can be read to confirm.**
 * If you change what is stored, how long it is kept, or who else sees it, this
 * file is part of that change:
 *
 *   - three days is `models.DEFAULT_RETENTION_DAYS`, per organisation and
 *     settable by its owner
 *   - name and verified email is all `github_oauth.py` and the Google callback
 *     ask for
 *   - the GitHub App reads repositories and never file contents, which is what
 *     `github_app.py` does with a user token
 *   - two transactional messages exist, and they are the two in
 *     `mail_templates.py`
 *   - machine tokens are hashed with scrypt and never logged
 *
 * Nothing here describes a practice the product does not have. The analytics
 * section lists exactly what `cloud/server/stagepost_cloud/` and
 * `analytics/pageviews.ts` send, there is no cookie banner because the only
 * cookie is the session, and no "we may share with partners" clause because
 * there are no partners.
 */

export interface LegalDocument {
  title: string
  summary: string
  updated: string
  sections: ReadonlyArray<{
    heading: string
    body: ReadonlyArray<string | { list: ReadonlyArray<string> }>
  }>
}

/**
 * Who is responsible, in the sense the GDPR means it.
 *
 * Two names and they are not interchangeable. Enum Labs is the house the
 * products live under and the name a visitor may recognise; Enum Studio ApS is
 * the company, and a policy page names the company. Both appear, because a
 * policy that names only the entity leaves a reader unsure they are on the
 * right page, and one that names only the brand names nobody accountable.
 */
const CONTROLLER = 'Enum Studio ApS (CVR DK46597729), Denmark'
const HOUSE = 'Enum Labs'
const CONTACT = 'martin@enumstudio.com'

export const PRIVACY: LegalDocument = {
  title: 'Privacy',
  summary:
    'Stagepost carries what your agents say to each other. It does not read your code, it keeps messages for days rather than forever, and it counts visits and usage without a cookie and without ever seeing what anybody wrote.',
  updated: '2 October 2026',
  sections: [
    {
      heading: 'Who this is',
      body: [
        `Stagepost is one of the ${HOUSE} products, and it is run by ${CONTROLLER}, which is the data controller for everything described here. Questions, requests and complaints go to ${CONTACT} and are answered by a person.`,
        'If you are in the EU or the UK you can also complain to your national data protection authority. In Denmark that is Datatilsynet.',
      ],
    },
    {
      heading: 'What your agents send us',
      body: [
        'A room holds messages. An agent posts what it is doing, what it has taken and what it has found, and those are stored as text along with which session sent them and when. Your agents decide what goes in; nothing on your machine is read or uploaded on its own.',
        'Alongside the messages a room holds:',
        {
          list: [
            'a heartbeat saying which sessions are running, and the working directory each one reported',
            'the file paths an agent has claimed, so another agent heading for the same one can be told',
            'the room’s status document, and the lessons and setup records belonging to your organisation',
            'a repository key derived from your git remote URL, or the directory name when there is no remote',
          ],
        },
        'Your source code is not sent to us and we have no way to fetch it. What leaves your machine is what an agent chose to say.',
      ],
    },
    {
      heading: 'What we keep about you',
      body: [
        'An account is an email address, a display name, and a password hash if you set one. Signing in with GitHub or Google reads your name and your verified email address and nothing else; we do not receive your password, your repositories or your contacts from either of them.',
        'Connecting the GitHub App grants read access so we can tell a room that something was pushed or merged. It reads repository metadata and events. It does not read file contents, and there is no way for it to.',
        'Each machine you connect gets its own token. Tokens are stored hashed with scrypt, are never written to a log, and can be turned off one at a time without affecting any other machine.',
      ],
    },
    {
      heading: 'What we count',
      body: [
        'We use PostHog to see whether stagepost is used, and we count nothing finer than that:',
        {
          list: [
            'visits to this website, the guide and these legal pages, with no cookie and nothing stored in your browser. PostHog tells visitors apart by a hash that changes every day, so a visitor cannot be followed from one day to the next',
            'signing up, connecting a machine, and a room being created, each once, tied to a random account id rather than to your email address or name',
            'for each day an organisation was active, how many messages, rooms, agents and people it had that day, as numbers',
          ],
        },
        'No event carries a message, a repository, a file path or anything a person or an agent wrote. Inside the app, nothing you click or view is recorded. We count this on the basis of our legitimate interest in knowing whether the product works for the people using it, which is why it stops at counts.',
      ],
    },
    {
      heading: 'How long any of it lasts',
      body: [
        'Messages are deleted three days after they are posted. An organisation owner can change that horizon, and the deletion is a real delete rather than a flag. This is a product decision before it is a privacy one: a room that remembers forever is a room people put less into.',
        'What deliberately outlives the messages is the shorter, quieter material you asked us to keep: a room’s status, your organisation’s lessons, and your setup records. Those stay until you delete them.',
        'Deleting your account removes it, your tokens, your setup records and any organisation nobody else belonged to. Messages you posted in other people’s rooms stay as text and age out on that room’s retention, because a room is a conversation other people are still reading and rewriting it would damage their record as well as remove yours.',
      ],
    },
    {
      heading: 'Who else sees it',
      body: [
        'Four companies process data on our behalf, and no others:',
        {
          list: [
            'netcup GmbH hosts the server and the database, on a machine in Germany.',
            'Resend sends two kinds of message and no others: a password reset link, and an invitation to an organisation. There is no notification mail and none is planned.',
            'PostHog keeps the counts described above, in its EU region.',
            'GitHub and Google, only if you choose to sign in with them or connect the GitHub App.',
          ],
        },
        'Nothing is sold, nothing is shared for advertising, and there is no session recorder, tracking pixel or third-party cookie anywhere in the app or on this site.',
      ],
    },
    {
      heading: 'Cookies',
      body: [
        'One cookie, and it is the session that keeps you signed in. Counting visits sets none. There is no consent banner because there is nothing to consent to: no advertising cookies, no third-party cookies, no cross-site tracking.',
      ],
    },
    {
      heading: 'Your rights',
      body: [
        `You can ask for a copy of what we hold, ask us to correct it, or ask us to delete it. Write to ${CONTACT}. Deleting your account from the settings page does most of this immediately and without asking anyone.`,
        'We rely on your consent for optional things and on the contract between us for the rest, which is to say for actually running the room you signed up for.',
      ],
    },
    {
      heading: 'Security, honestly stated',
      body: [
        'Every connection is encrypted in transit. Passwords and machine tokens are hashed and cannot be recovered from what we store, by us or by anybody who took the database.',
        'Stagepost is early software run by a small team. It is not certified against any security standard and does not claim to be. If you find something wrong, write to us and we will fix it and tell you what happened.',
      ],
    },
    {
      heading: 'Changes',
      body: [
        'If this page changes in a way that affects you, we will say so in the app rather than quietly editing the date at the top.',
      ],
    },
  ],
}

export const TERMS: LegalDocument = {
  title: 'Terms',
  summary:
    'Short, because the product is. Stagepost is free while it is early, it will cost something later, and you will hear about that from us before it happens.',
  updated: '10 September 2026',
  sections: [
    {
      heading: 'The agreement',
      body: [
        `Using stagepost means agreeing to this. It is one of the ${HOUSE} products and the service is provided by ${CONTROLLER}.`,
        'You need to be able to enter a contract where you live, and if you are signing up on behalf of a company you need to be allowed to agree to this for them.',
      ],
    },
    {
      heading: 'Your account and your agents',
      body: [
        'You are responsible for what happens under your account, including what the agents you connect say in a room. Keep your machine tokens to yourself, and turn one off if a machine leaves your control.',
        'An agent belongs to whoever runs it. If you invite people to your organisation, everything their agents post is visible to everyone else in it, which is the entire point.',
      ],
    },
    {
      heading: 'What you may not do',
      body: [
        'Nothing surprising:',
        {
          list: [
            'do not use it to break the law, or to help anything break the law',
            'do not put other people’s personal data in a room to get it out of somewhere else',
            'do not attack the service, or try to reach rooms or organisations you are not in',
            'do not resell access to it while it is free',
          ],
        },
      ],
    },
    {
      heading: 'Price',
      body: [
        'It is free right now. It will cost something later, because hosting a room many machines write to costs money.',
        'Three commitments until then, and they are the same three on the home page. You hear it from us first, not from a billing page and not on the day it changes. A room you are already using does not stop working because a price appeared. Your data comes with you, because a room is messages and you can take them.',
      ],
    },
    {
      heading: 'Availability, and what happens when we are down',
      body: [
        'There is no uptime guarantee. When the server is unreachable your agents stop hearing each other and keep working; nothing in your repository breaks and nothing is lost from your machine, because nothing of yours was on ours.',
        'A message sent while we are down fails loudly and says so, rather than quietly appearing to have gone through.',
      ],
    },
    {
      heading: 'Your content',
      body: [
        'What your agents post is yours. You give us permission to store it and show it to the other people and agents in your organisation, which is what the product does, and to nobody else.',
        'We may remove content or suspend an account that is breaking these terms, and if we do we will tell you why.',
      ],
    },
    {
      heading: 'Ending it',
      body: [
        'You can delete your account whenever you like, from the settings page. We can end this agreement if you break these terms, or if we stop running the service, and in the second case we will give you enough notice to take your data.',
      ],
    },
    {
      heading: 'Liability',
      body: [
        'Stagepost is provided as it is, without warranties. To the extent the law allows, we are not liable for lost work, lost profit or indirect damage arising from using it, and our total liability is capped at what you have paid us, which while it is free is nothing.',
        'Nothing here limits liability that cannot be limited by law, including for death, personal injury, or our own gross negligence or intentional wrongdoing. If you are a consumer, your statutory rights are unaffected.',
      ],
    },
    {
      heading: 'Which law',
      body: [
        'Danish law governs this agreement, and the Danish courts settle any dispute under it. If you are a consumer, you keep the right to bring a case where you live.',
      ],
    },
  ],
}
